import appLogo from "@/assets/images/app_logo.png";
import {
  AlltimeEntriesSVG,
  ClientSVG,
  LogoutSVG,
  ProjectSVG,
  ProjectTrackingSVG,
  ReportsSVG,
  TimeEntrySVG,
  UserManagementSVG,
  UserProfileChevronDownSVG,
  UserProfileChevronUpSVG,
  UserProfileSVG,
  EmailSvg,
} from "@/assets/svg";
import Breadcrumb from "@/components/Breadcrumb";
import OrganizationDropdown from "@/components/OrganizationDropdown";
import { useOverlay } from "@/context/OverlayContext";
import { useAppDispatch } from "@/hooks";
import { RootState } from "@/store";
import { logout, selectUserRole } from "@/store/slices/authSlice";
import {
  connectQuickbooks,
  disconnectQuickbooks,
  syncQuickbooksDataAction,
} from "@/store/slices/quickbookConnectSlice";
import { MENU_PERMISSIONS, ROLES, hasMenuPermission } from "@/utils/constants";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router";

const Loader = () => (
  <svg
    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);
interface AppLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
  rightActions?: ReactNode;
  contentClassName?: string;
  showBreadcrumb?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title,
  description,
  rightActions,
  contentClassName,
  showBreadcrumb = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const userRole = useSelector(selectUserRole);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Get overlay state from context
  const { isOverlayActive } = useOverlay();

  // Handle click outside to close popup and sidebar
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Handle popup close
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowLogoutPopup(false);
      }

      // Handle sidebar close on mobile
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        window.innerWidth < 768 // Only close on mobile
      ) {
        setSidebarOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef, sidebarRef]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await dispatch(logout(navigate) as any);
    } catch (error) {
      console.error("Logout failed:", error);
      // Still navigate to sign in page even if logout API fails
      // navigate("/auth/signin");
    }
  };

  // Define all navigation items with permission requirements
  const baseNavigationItems = [
    {
      name: "Projects",
      path: "/projects",
      icon: (isActive: boolean) => <ProjectSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.PROJECTS,
    },
    {
      name: "Clients",
      path: "/clients",
      icon: (isActive: boolean) => <ClientSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.CLIENTS,
    },
    {
      name: "Employee management",
      path: "/employee-management",
      icon: (isActive: boolean) => <UserManagementSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.USER_MANAGEMENT,
    },
  ];

  // Define report-related items
  const reportItems = [
    {
      name: "All time entries",
      path: "/reports/time-entries",
      icon: (isActive: boolean) => <AlltimeEntriesSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.ALL_TIME_ENTRIES,
      isReportItem: true,
    },
    {
      name: "Project tracking report",
      path: "/reports/project-tracking",
      icon: (isActive: boolean) => <ProjectTrackingSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.PROJECT_TRACKING,
      isReportItem: true,
    },
  ];

  // Time entry as a separate item
  const timeEntryItem = {
    name: "Time entry",
    path: "/time-entry",
    icon: (isActive: boolean) => <TimeEntrySVG active={isActive} />,
    permissions: MENU_PERMISSIONS.TIME_ENTRY,
  };

  // Reports menu item (only for admin)
  const reportsMenuItem = {
    name: "Reports",
    path: "/reports",
    icon: (isActive: boolean) => <ReportsSVG active={isActive} />,
    permissions: MENU_PERMISSIONS.REPORTS,
  };

  const emailMenuItem = {
    name: "Email",
    path: "/email",
    icon: (isActive: boolean) => <EmailSvg active={isActive} />,
    permissions: MENU_PERMISSIONS.EMAIL,
  };

  // Determine if user is admin or client
  const isAdmin = userRole === ROLES.ADMIN;
  const isClient = userRole === ROLES.CLIENT;

  // Build the navigation items based on role
  let allNavigationItems = [...baseNavigationItems];

  // Add time entry to navigation for roles with permission
  if (hasMenuPermission(userRole, timeEntryItem.permissions)) {
    allNavigationItems.push(timeEntryItem);
  }

  // For admin, add the Reports menu item
  if (isAdmin) {
    allNavigationItems.push(reportsMenuItem, emailMenuItem, {
      name: "Email template",
      path: "/email-templates",
      icon: (isActive: boolean) => <EmailSvg active={isActive} />,
      permissions: MENU_PERMISSIONS.EMAIL,
    });
  }
  // For non-admin roles with ALL_TIME_ENTRIES permission, add it directly to navigation
  else if (hasMenuPermission(userRole, MENU_PERMISSIONS.ALL_TIME_ENTRIES)) {
    allNavigationItems.push({
      name: "All time entries",
      path: "/reports/time-entries",
      icon: (isActive: boolean) => <AlltimeEntriesSVG active={isActive} />,
      permissions: MENU_PERMISSIONS.ALL_TIME_ENTRIES,
    });
  }

  // Filter navigation items based on user role
  const navigationItems = allNavigationItems.filter((item) =>
    hasMenuPermission(userRole, item.permissions)
  );

  // Only show report items in submenu for admin
  const filteredReportItems = isAdmin
    ? reportItems.filter((item) =>
      hasMenuPermission(userRole, item.permissions)
    )
    : [];

  const isConnected = useSelector(
    (state: RootState) => state.quickbookConnect.isConnected,
    shallowEqual
  );

  const quickBookLoader = useSelector(
    (state: RootState) => state.quickbookConnect.isLoading,
    shallowEqual
  );

  const syncLoading = useSelector(
    (state: RootState) => state.quickbookConnect.syncLoading,
    shallowEqual
  );

  const connectQuickBooks = () => {
    dispatch(connectQuickbooks())
      .unwrap()
      .then(() => { });
  };

  const syncQuickBooks = () => {
    dispatch(syncQuickbooksDataAction())
      .unwrap()
      .then(() => { });
  };

  const disconnectQuickBooks = () => {
    dispatch(disconnectQuickbooks())
      .unwrap()
      .then(() => { });
  };
  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Global overlay for clock-in active state */}
      {isOverlayActive && (
        <div className="fixed inset-0 z-50" onClick={() => { }}>
          {/* Transparent hole for the active time entry component */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-4xl h-[80%] max-h-[600px] bg-transparent pointer-events-none" />
        </div>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
          style={{
            backgroundColor: "rgba(75, 86, 102, 0.5)",
          }}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 w-64 bg-white border-gray-200 flex flex-col fixed md:static h-full z-30 transition-transform duration-300 ease-in-out`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200 h-[70px]">
          <Link to="/dashboard" className="flex items-center justify-center">
            <img
              src={appLogo}
              alt="HLJ Creative"
              className="h-[45px] w-[177.83px]"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.path} className="border-b border-gray-200">
                {item.path === "/reports" ? (
                  <>
                    <span className="flex items-center px-4 py-3 text-[14px] rounded-md font-[600] text-[#6C6C6C]">
                      <span className="mr-3">{item.icon(false)}</span>
                      {item.name}
                    </span>
                    {isAdmin && filteredReportItems.length > 0 && (
                      <div className="py-1">
                        <ul className="space-y-1">
                          {filteredReportItems.map((subItem) => (
                            <li
                              key={subItem.path}
                              className="pl-4 border-b h-[48px] border-gray-200"
                            >
                              <Link
                                to={subItem.path}
                                className={`flex items-center px-4 py-3 text-[14px] rounded-md font-[600] ${location.pathname === subItem.path ||
                                    (subItem.path === "/reports/project-tracking" &&
                                      location.pathname.startsWith(
                                        "/reports/project-tracking/"
                                      ))
                                    ? "bg-opacity-10 text-[#73B5C5]"
                                    : "text-[#6C6C6C] hover:bg-gray-100"
                                  }`}
                              >
                                <span className="mr-3">
                                  {subItem.icon(
                                    location.pathname === subItem.path ||
                                    (subItem.path === "/reports/project-tracking" &&
                                      location.pathname.startsWith(
                                        "/reports/project-tracking/"
                                      ))
                                  )}
                                </span>
                                {subItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-3 text-[14px] rounded-md font-[600] ${location.pathname === item.path ||
                        (item.path === "/reports/project-tracking" &&
                          location.pathname.startsWith(
                            "/reports/project-tracking/"
                          ))
                        ? "bg-opacity-10 text-[#73B5C5]"
                        : "text-[#6C6C6C] hover:bg-gray-100"
                      }`}
                  >
                    <span className="mr-3">
                      {item.icon(
                        location.pathname === item.path ||
                        (item.path === "/reports/project-tracking" &&
                          location.pathname.startsWith(
                            "/reports/project-tracking/"
                          ))
                      )}
                    </span>
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-[70px] bg-white border-b border-gray-200 py-4 px-6 flex items-center justify-between md:justify-end">
          {/* Client Organization Dropdown */}
          {isClient && <OrganizationDropdown />}
          {isAdmin && (
            <>
              {isConnected ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={syncQuickBooks}
                    className="bg-[#2ba01d] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1 mr-3"
                  >
                    {syncLoading ? (
                      <>
                        <Loader />
                        Syncing...
                      </>
                    ) : (
                      "Sync Client Data"
                    )}
                  </button>
                </div>
              ) : (
                <button
                  onClick={connectQuickBooks}
                  className="bg-[#2ba01d] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1 mr-3"
                >
                  {quickBookLoader ? (
                    <>
                      <Loader />
                      Connecting...
                    </>
                  ) : (
                    "Connect to QuickBooks"
                  )}
                </button>
              )}
            </>
          )}

          {/* Mobile menu button */}
          <button
            className="md:hidden text-gray-600 focus:outline-none cursor-pointer"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* User profile */}
          <div className="relative">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => setShowLogoutPopup(!showLogoutPopup)}
            >
              <UserProfileSVG />
              <div className="mr-[13px] ml-[4px]">
                <p className="font-[500] text-[14px] text-[#1E1E1E]">
                  {user?.name || "User"}
                </p>
                <p className="text-[#6C6C6C] text-[12px]">
                  {user?.email || user?.username || "user@example.com"}
                </p>
              </div>
              {!showLogoutPopup ? (
                <UserProfileChevronUpSVG />
              ) : (
                <UserProfileChevronDownSVG />
              )}
            </div>

            {/* Logout popup */}
            {showLogoutPopup && (
              <div
                ref={popupRef}
                className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-12 border border-gray-200"
              >
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-[14px] text-[#1E1E1E] hover:bg-gray-100 cursor-pointer"
                >
                  <LogoutSVG className="mr-[8px]" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 flex flex-col overflow-hidden bg-gray-50">
          {/* Fixed title header */}
          <div className="bg-white w-full h-[70px] pt-[12px] pr-[20px] md:pr-[40px] pb-[12px] pl-[16px] md:pl-[24px] flex items-center justify-between flex-shrink-0 z-11 shadow-sm">
            <div className="flex flex-col max-w-[70%] md:max-w-full">
              <h1 className="font-[600] text-[#1E1E1E] text-[20px] truncate">
                {title}
              </h1>
              {description && (
                <h1 className="font-[500] text-[#6C6C6C] text-[10px] md:text-[12px] mt-[4px] md:mt-[6px] truncate">
                  {description}
                </h1>
              )}

              {showBreadcrumb && <Breadcrumb className="mb-1" />}
            </div>
            {rightActions}
          </div>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto">
            <div
              className={`bg-white rounded-md items-center m-2 md:m-6 ${contentClassName || ""
                }`}
              style={{
                boxShadow: "0px 10px 60px 0px rgba(226, 236, 249, 0.5)",
              }}
            >
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
